import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { apiClient } from 'shared/api';
import { useUser } from 'shared/api/tanstack/queries';
import styles from './referals.module.scss';

interface Referal {
  id: number;
  documentId: string;
  bio?: string;
  email?: string;
  phone?: string;
  inn?: string;
  quantity?: string;
  car_type?: string;
  car_subtype?: string;
  car_brand?: string;
  vehicle?: string;
  pay_method?: string;
  condition?: string;
  // Поля для лизинговых заявок
  price?: string;
  initial_payment?: string;
  lease_period?: string;
  payment_type?: string;
  createdAt: string;
  updatedAt: string;
  inviter: string;
}

export const Referals = () => {
  const [referals, setReferals] = useState<Referal[]>([]);
  const [filteredReferals, setFilteredReferals] = useState<Referal[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: user } = useUser();

  // Состояния для поисковых фильтров
  const [searchFilters, setSearchFilters] = useState({
    bio: '',
    email: '',
    inn: '',
    phone: '',
  });

  // Функция фильтрации данных
  const filterReferals = (referals: Referal[], filters: typeof searchFilters) => {
    return referals.filter((referal) => {
      const bioMatch =
        !filters.bio || referal.bio?.toLowerCase().includes(filters.bio.toLowerCase());
      const emailMatch =
        !filters.email || referal.email?.toLowerCase().includes(filters.email.toLowerCase());
      const innMatch =
        !filters.inn || referal.inn?.toLowerCase().includes(filters.inn.toLowerCase());
      const phoneMatch =
        !filters.phone || referal.phone?.toLowerCase().includes(filters.phone.toLowerCase());

      return bioMatch && emailMatch && innMatch && phoneMatch;
    });
  };

  // Обработчик изменения поисковых фильтров
  const handleFilterChange = (field: keyof typeof searchFilters, value: string) => {
    const newFilters = { ...searchFilters, [field]: value };
    setSearchFilters(newFilters);

    // Применяем фильтры к текущему списку
    const filteredReferals = filterReferals(referals, newFilters);
    setFilteredReferals(filteredReferals);
  };

  const fetchReferals = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get('/agents/getReferals');
      const data = response.data || [];
      setReferals(data);
      setFilteredReferals(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке черновиков:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchReferals();
    }
  }, [user?.id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderEmptyState = () => (
    <Box className={styles.emptyState}>
      <Typography variant='h6' color='text.secondary' align='center'>
        Нет рефералов
      </Typography>
    </Box>
  );

  const renderTable = () => {
    const referals = filteredReferals;
    const originalReferals = referals;

    // Показываем полное пустое состояние только если нет черновиков вообще
    if (originalReferals.length === 0) {
      return renderEmptyState();
    }

    return (
      <Box>
        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label='Список рефералов'>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell className={styles.tableHeadCell}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='ФИО'
                    value={searchFilters.bio}
                    onChange={(e) => handleFilterChange('bio', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='email'
                    value={searchFilters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='ИНН'
                    value={searchFilters.inn}
                    onChange={(e) => handleFilterChange('inn', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='телефону'
                    value={searchFilters.phone}
                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                />
              </TableRow>
              <TableRow className={styles.tableHeadRow}>
                <TableCell className={styles.tableHeadCell}>ФИО</TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  ИНН
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Email
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Телефон
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Пригласил
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {referals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align='center' style={{ padding: '40px 20px' }}>
                    <Typography variant='h6' color='text.secondary'>
                      По вашему запросу ничего не найдено
                    </Typography>
                    <Typography variant='body2' color='text.secondary' style={{ marginTop: '8px' }}>
                      Попробуйте изменить критерии поиска
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                referals.map((referal) => {
                  return (
                    <TableRow
                      key={referal.id}
                      className={styles.tableBodyRow}
                      sx={{
                        '& td, & th': { border: 0 },
                        marginTop: '10px',
                      }}
                    >
                      <TableCell className={styles.tableBodyCell} component='th' scope='row'>
                        {referal.bio || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {referal.inn || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {referal.email || '-'}
                      </TableCell>
                      <TableCell
                        className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                        align='left'
                      >
                        {referal.phone || '-'}
                      </TableCell>
                      <TableCell
                        className={styles.tableBodyCell}
                        align='center'
                        style={{ cursor: 'pointer' }}
                      >
                        <Typography variant='caption'>{referal.inviter || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box className={styles.container}>
        <Typography variant='h4' className={styles.pageTitle}>
          Рефералы
        </Typography>
        <Box className={styles.loading}>
          <Typography>Загрузка...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography variant='h4' className={styles.pageTitle}>
        Рефералы
      </Typography>

      {renderTable()}
    </Box>
  );
};
